/**
 * Admin module: Acconti dipendenti.
 *
 * Two main pages mounted under different routes:
 *   /acconti           → list + filters + create new
 *   /acconti/:id       → detail (view/edit + PDF receipt)
 *
 * Substitutes the legacy `Acconti 2026.xlsm` workflow.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { buildPlanProposals, planLabel } from "../lib/installments";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../components/ui/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import {
    Plus,
    Search,
    Wallet,
    Loader2,
    RefreshCw,
    Euro,
    Calendar,
    TrendingUp,
    Users,
    Eye,
    FileSearch,
    Banknote,
    FileSpreadsheet,
    Settings,
    CheckCircle2,
    Clock,
    PiggyBank,
    AlertTriangle,
    CalendarOff,
} from "lucide-react";
import { FileSignature } from "lucide-react";
import { toast } from "sonner";
import { fmtEur } from "../lib/format";
import { AdvancePdfPreviewDialog } from "../components/AdvancePdfPreviewDialog";
import { BustaPagaSelector } from "../components/BustaPagaSelector";
import { TfrBalanceBox } from "../components/tfr/TfrBalanceBox";
import {
    InstallmentScheduleEditor,
    buildConsecutiveLabels,
    evenSplit,
    validateScheduleRows,
    round2,
} from "../components/InstallmentScheduleEditor";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";

const TYPES = [
    { value: "ACCONTO", label: "Acconto" },
    { value: "FNG", label: "F.N.G." },
    { value: "TFR", label: "T.F.R." },
    { value: "PRESTITO", label: "Prestito" },
    { value: "ANF", label: "A.N.F." },
    { value: "EMOLUMENTI", label: "Emolumenti" },
    { value: "ACCONTO_FERIE_NON_GODUTE", label: "Acconto ferie non godute" },
    { value: "ACCONTO_TFR", label: "Acconto T.F.R." },
];

const METHODS = [
    { value: "BONIFICO", label: "Bonifico" },
    { value: "BON_ISTAN", label: "Bonifico istantaneo" },
    { value: "ASSEGNO", label: "Assegno" },
    { value: "CONTANTI", label: "Contanti" },
];

const METHOD_LABELS = Object.fromEntries(METHODS.map((m) => [m.value, m.label]));

const HandCoins = Banknote;

const STATUSES = [
    { value: "fully_recovered", label: "Recuperato" },
    { value: "in_progress", label: "In recupero" },
    { value: "not_started", label: "Da recuperare" },
    { value: "cancelled", label: "Annullato" },
];

const STATUS_COLORS = {
    issued: "bg-blue-50 text-blue-700 border-blue-200",
    signed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    recovered: "bg-slate-100 text-slate-700 border-slate-300",
    cancelled: "bg-red-50 text-red-700 border-red-200",
    draft: "bg-amber-50 text-amber-700 border-amber-200",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
        });
    } catch { return iso; }
};

function RecoveryProgress({ advance }) {
    const total = advance.installments_total || 1;
    const done = advance.installments_recovered || 0;
    const pct = advance.progress_pct || 0;
    let color, label;
    if (advance.is_fully_recovered) {
        color = "bg-emerald-500";
        label = "Recuperato";
    } else if (advance.is_not_started) {
        color = "bg-slate-300";
        label = "Da recuperare";
    } else {
        color = "bg-amber-500";
        label = `${done}/${total} rate`;
    }
    return (
        <div className="min-w-[110px]">
            <div className="flex items-center justify-between text-[10px] text-slate-600">
                <span className="font-medium">{label}</span>
                <span className="font-mono">{pct.toFixed(0)}%</span>
            </div>
            <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full ${color} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            {advance.amount_remaining > 0 && !advance.is_fully_recovered && (
                <div className="mt-0.5 text-[10px] text-slate-500">
                    residuo {fmtEur(advance.amount_remaining)}
                </div>
            )}
        </div>
    );
}


function SignatureBadge({ advance }) {
    if (advance.signed_at || advance.status === "signed") {
        return (
            <div data-testid={`sign-status-${advance.id}`}>
                <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 px-1.5 text-[10px] font-semibold text-emerald-700">
                    <CheckCircle2 className="h-3 w-3" />
                    Firmata
                </Badge>
                {advance.signed_at && (
                    <div className="mt-0.5 text-[9px] text-slate-400">{fmtDate(advance.signed_at)}</div>
                )}
            </div>
        );
    }
    if (advance.legacy_import || advance.status === "cancelled" || advance.status === "draft") {
        return <span className="text-[10px] text-slate-400" data-testid={`sign-status-${advance.id}`}>—</span>;
    }
    return (
        <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 px-1.5 text-[10px] font-semibold text-amber-700" data-testid={`sign-status-${advance.id}`}>
            <Clock className="h-3 w-3" />
            Da firmare
        </Badge>
    );
}

const ADV_FILTERS_KEY = "acconti_filters";
const readAdvFilters = () => {
    try {
        return JSON.parse(sessionStorage.getItem(ADV_FILTERS_KEY)) || {};
    } catch {
        return {};
    }
};


export default function AdvancesPage() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    // Explicit URL params (e.g. link from employee card) win; otherwise restore the last used filters
    const saved = [...params.keys()].length ? {} : readAdvFilters();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [year, setYear] = useState(params.get("year") || saved.year || String(new Date().getFullYear()));
    const [type, setType] = useState(params.get("type") || saved.type || "");
    const [status, setStatus] = useState(() => {
        const raw = params.get("status") || saved.status || "";
        // Map legacy workflow-status values to new business-state values
        const VALID = new Set(["fully_recovered", "in_progress", "not_started", "cancelled"]);
        if (VALID.has(raw)) return raw;
        if (raw === "recovered") return "fully_recovered";
        return "";
    });
    const [employeeId, setEmployeeId] = useState(params.get("employee_id") || saved.employee_id || "");
    const [search, setSearch] = useState(saved.search || "");
    const [createOpen, setCreateOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [previewAdvance, setPreviewAdvance] = useState(null);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [approveRequest, setApproveRequest] = useState(null);
    const [rejectRequest, setRejectRequest] = useState(null);
    const [rejectNote, setRejectNote] = useState("");
    const [rejectBusy, setRejectBusy] = useState(false);
    const [signPanelOpen, setSignPanelOpen] = useState(false);
    const [tfrPanelOpen, setTfrPanelOpen] = useState(false);
    const [tfrReminder, setTfrReminder] = useState(null);
    useEffect(() => {
        api.get("/tfr/reminder-status").then((r) => setTfrReminder(r.data)).catch(() => {});
    }, []);

    const unsignedTfrDecls = useMemo(() => {
        const now = Date.now();
        return items
            .filter((x) => ["TFR", "ACCONTO_TFR"].includes(x.type) && !x.tfr_decl_signed_at
                && !x.legacy_import && !["cancelled", "draft"].includes(x.status))
            .map((x) => ({
                ...x,
                waitingDays: Math.max(0, Math.floor((now - new Date(x.created_at).getTime()) / 86400000)),
            }))
            .sort((a, b) => b.waitingDays - a.waitingDays);
    }, [items]);

    const unsignedReceipts = useMemo(() => {
        const now = Date.now();
        return items
            .filter((x) => x.status === "issued" && !x.legacy_import)
            .map((x) => ({
                ...x,
                waitingDays: Math.max(0, Math.floor((now - new Date(x.created_at).getTime()) / 86400000)),
            }))
            .sort((a, b) => b.waitingDays - a.waitingDays);
    }, [items]);

    const loadRequests = () => {
        api.get("/advance-requests", { params: { status: "pending" } })
            .then((r) => setPendingRequests(r.data || []))
            .catch(() => {});
    };

    const handleReject = async () => {
        setRejectBusy(true);
        try {
            await api.post(`/advance-requests/${rejectRequest.id}/review`, {
                action: "reject",
                admin_note: rejectNote.trim() || null,
            });
            toast.success("Richiesta rifiutata");
            setRejectRequest(null);
            setRejectNote("");
            loadRequests();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore rifiuto richiesta");
        } finally {
            setRejectBusy(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const q = {};
            if (year && year !== "all") q.year = year;
            if (type) q.type = type;
            // NOTE: `status` filter is applied client-side using computed business
            // state (is_fully_recovered / is_in_progress / is_not_started / cancelled)
            // rather than the raw workflow status, so we don't send it to the backend.
            if (employeeId) q.employee_id = employeeId;
            if (search) q.search = search;
            const { data } = await api.get("/advances", { params: q });
            setItems(data);
        } catch (e) {
            toast.error("Errore nel caricamento acconti");
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const { data } = await api.get("/employees");
            setEmployees(data);
        } catch (e) {
            /* ignore */
        }
    };

    useEffect(() => {
        fetchEmployees();
        loadRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchData();
        // sync URL params
        const p = {};
        if (year) p.year = year;
        if (type) p.type = type;
        if (status) p.status = status;
        if (employeeId) p.employee_id = employeeId;
        setParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [year, type, status, employeeId]);

    // Debounced search
    useEffect(() => {
        const t = setTimeout(() => fetchData(), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // Client-side STATO filter using computed business state
    const filteredItems = useMemo(() => {
        if (!status) return items;
        return items.filter((x) => {
            if (status === "fully_recovered") return x.is_fully_recovered === true;
            if (status === "in_progress") return x.is_in_progress === true;
            if (status === "not_started") return x.is_not_started === true && !x.is_fully_recovered;
            if (status === "cancelled") return x.status === "cancelled";
            return true;
        });
    }, [items, status]);

    // KPI computed from filtered set
    const stats = useMemo(() => {
        const totalErogato = filteredItems.reduce((s, x) => s + (x.amount || 0), 0);
        const totalRecovered = filteredItems.reduce((s, x) => s + (x.amount_recovered || 0), 0);
        const totalRemaining = filteredItems.reduce((s, x) => s + (x.amount_remaining || 0), 0);
        const inProgress = filteredItems.filter((x) => x.is_in_progress).length;
        const notStarted = filteredItems.filter((x) => x.is_not_started && !x.is_fully_recovered).length;
        const fullyRecovered = filteredItems.filter((x) => x.is_fully_recovered).length;
        const distinctEmps = new Set(filteredItems.map((x) => x.employee_id)).size;
        return {
            count: filteredItems.length,
            totalErogato,
            totalRecovered,
            totalRemaining,
            inProgress,
            notStarted,
            fullyRecovered,
            distinctEmps,
        };
    }, [filteredItems]);

    const years = useMemo(() => {
        const cur = new Date().getFullYear();
        const list = [];
        for (let y = cur + 1; y >= cur - 4; y--) list.push(String(y));
        return list;
    }, []);

    const { sortedItems: itemsSorted, sortKey, sortDir, requestSort } = useSortableTable(filteredItems, {
        customAccessors: {
            dipendente: (a) => `${a.employee_label || ""}`.toLowerCase(),
            tipo: (a) => a.type_label || "",
            recupero: (a) => a.bp_init_label || "",
            firma: (a) => (a.signed_at || a.status === "signed" ? 2 : a.legacy_import || a.status === "cancelled" || a.status === "draft" ? 1 : 0),
        },
        initialSort: { key: saved.sortKey || null, dir: saved.sortDir || "asc" },
    });

    // Remember filters + sort so "Torna all'elenco" restores the same view
    useEffect(() => {
        sessionStorage.setItem(
            ADV_FILTERS_KEY,
            JSON.stringify({ year, type, status, employee_id: employeeId, search, sortKey, sortDir })
        );
    }, [year, type, status, employeeId, search, sortKey, sortDir]);

    return (
        <div className="mx-auto max-w-7xl space-y-4 px-4 py-8 sm:px-6 lg:px-8" data-testid="advances-page">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h1 className="font-display text-xl font-bold text-[#15498F]">
                        Acconti dipendenti
                    </h1>
                    <p className="text-xs text-slate-500">
                        Gestione ricevute di acconti, prestiti e recuperi mensili.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Link to="/acconti/tfr">
                        <Button variant="outline" size="sm" className="gap-2 text-xs" data-testid="tfr-page-btn">
                            <PiggyBank className="h-3.5 w-3.5" />
                            Fondo TFR
                        </Button>
                    </Link>
                    <Link to="/acconti/fng">
                        <Button variant="outline" size="sm" className="gap-2 text-xs" data-testid="fng-page-btn">
                            <CalendarOff className="h-3.5 w-3.5" />
                            F.N.G.
                        </Button>
                    </Link>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExportOpen(true)}
                        className="gap-2 text-xs"
                        data-testid="export-report-btn"
                    >
                        <FileSpreadsheet className="h-3.5 w-3.5" />
                        Riepilogo consulente
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setCreateOpen(true)}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90 text-xs"
                        data-testid="new-advance-btn"
                    >
                        <Plus className="h-3.5 w-3.5" />
                        Nuovo acconto
                    </Button>
                </div>
            </div>

            {/* Promemoria saldi TFR del nuovo anno (dal 20 gennaio) */}
            {tfrReminder?.due && (
                <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid="tfr-reminder-banner-acconti">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                        <b>Saldi TFR {tfrReminder.year} non ancora inseriti.</b> Carica i saldi al {tfrReminder.prev_year_end} del consulente nella pagina{" "}
                        <Link to="/acconti/tfr" className="font-semibold underline">Fondo TFR</Link> per mostrare il residuo disponibile in fase di approvazione degli anticipi TFR.
                    </div>
                </div>
            )}

            {/* Richieste di acconto dai dipendenti */}
            {pendingRequests.length > 0 && (
                <div
                    className="rounded-lg border border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm"
                    data-testid="advance-requests-section"
                >
                    <div className="mb-2 flex items-center gap-2">
                        <HandCoins className="h-4 w-4 text-emerald-600" />
                        <span className="font-display text-sm font-bold text-emerald-900">
                            {pendingRequests.length === 1
                                ? "1 richiesta di acconto in attesa"
                                : `${pendingRequests.length} richieste di acconto in attesa`}
                        </span>
                    </div>
                    <ul className="space-y-2">
                        {pendingRequests.map((r) => (
                            <li
                                key={r.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-3 py-2 shadow-sm ring-1 ring-emerald-100"
                                data-testid={`advance-request-row-${r.id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm">
                                        <b>{r.employee_label || "—"}</b>
                                        <span className="ml-2 font-semibold text-emerald-700">€ {Number(r.amount).toFixed(2)}</span>
                                        {r.preferred_payment_method && (
                                            <span className="ml-2 text-xs text-slate-500">
                                                ({METHOD_LABELS[r.preferred_payment_method] || r.preferred_payment_method})
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-0.5 text-xs text-slate-500">
                                        {r.reason} · richiesto il {(r.requested_at || "").slice(0, 10).split("-").reverse().join("/")}
                                    </div>
                                    {r.recovery_target && (
                                        <div className="mt-0.5 text-xs font-medium text-emerald-800">
                                            Recupero: {r.recovery_target === "FNG" ? "Ferie non godute (F.N.G.)"
                                                : r.recovery_target === "TFR" ? "T.F.R."
                                                : `busta paga da ${r.recovery_month || "—"}${(r.recovery_months_count || 1) > 1 ? ` in ${r.recovery_months_count} mensilità` : ""}`}
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-shrink-0 gap-1.5">
                                    <Button
                                        size="sm"
                                        onClick={() => setApproveRequest(r)}
                                        className="h-8 gap-1.5 bg-emerald-600 px-3 text-xs text-white hover:bg-emerald-700"
                                        data-testid={`advance-request-approve-${r.id}`}
                                    >
                                        <CheckCircle2 className="h-3.5 w-3.5" /> Approva
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => { setRejectRequest(r); setRejectNote(""); }}
                                        className="h-8 gap-1.5 border-red-200 px-3 text-xs text-red-600 hover:bg-red-50"
                                        data-testid={`advance-request-reject-${r.id}`}
                                    >
                                        Rifiuta
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* KPI Cards — compact 6 columns, clickable to filter by business state */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                <KpiCard icon={Wallet} label="Ricevute" value={stats.count} color="blue" testId="kpi-count" />
                <KpiCard icon={Euro} label="Erogato" value={fmtEur(stats.totalErogato)} color="blue" testId="kpi-total" />
                <KpiCard
                    icon={CheckCircle2}
                    label="Recuperato"
                    value={fmtEur(stats.totalRecovered)}
                    color="emerald"
                    testId="kpi-recovered"
                    active={status === "fully_recovered"}
                    onClick={() => setStatus(status === "fully_recovered" ? "" : "fully_recovered")}
                />
                <KpiCard
                    icon={Clock}
                    label="Da recuperare"
                    value={fmtEur(stats.totalRemaining)}
                    color="amber"
                    testId="kpi-remaining"
                    active={status === "not_started"}
                    onClick={() => setStatus(status === "not_started" ? "" : "not_started")}
                />
                <KpiCard
                    icon={TrendingUp}
                    label="In corso"
                    value={stats.inProgress}
                    color="slate"
                    testId="kpi-in-progress"
                    active={status === "in_progress"}
                    onClick={() => setStatus(status === "in_progress" ? "" : "in_progress")}
                />
                <KpiCard icon={Users} label="Dipendenti" value={stats.distinctEmps} color="slate" testId="kpi-employees" />
            </div>

            {/* Cruscotto firme: ricevute in attesa di firma */}
            {unsignedReceipts.length > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50/70 p-3" data-testid="sign-dashboard">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <Clock className="h-4 w-4 text-amber-600" />
                            <span className="font-semibold text-amber-900">
                                {unsignedReceipts.length === 1
                                    ? "1 ricevuta in attesa di firma"
                                    : `${unsignedReceipts.length} ricevute in attesa di firma`}
                            </span>
                            <span className="text-xs text-amber-700">
                                — la più vecchia da {unsignedReceipts[0].waitingDays}{" "}
                                {unsignedReceipts[0].waitingDays === 1 ? "giorno" : "giorni"}
                            </span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 border-amber-300 bg-white px-2.5 text-xs text-amber-800 hover:bg-amber-100"
                            onClick={() => setSignPanelOpen(!signPanelOpen)}
                            data-testid="sign-dashboard-toggle"
                        >
                            {signPanelOpen ? "Nascondi elenco" : "Mostra elenco"}
                        </Button>
                    </div>
                    {signPanelOpen && (
                        <ul className="mt-2 divide-y divide-amber-100 rounded-md bg-white shadow-sm ring-1 ring-amber-100">
                            {unsignedReceipts.map((a) => (
                                <li
                                    key={a.id}
                                    className="flex cursor-pointer flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-amber-50"
                                    onClick={() => navigate(`/acconti/${a.id}`)}
                                    data-testid={`sign-pending-row-${a.id}`}
                                >
                                    <div className="min-w-0">
                                        <b>#{a.receipt_label}</b>
                                        <span className="ml-2">{a.employee_label}</span>
                                        <span className="ml-2 font-semibold text-slate-700">{fmtEur(a.amount)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span
                                            className={`rounded-full px-2 py-0.5 font-semibold ${
                                                a.waitingDays >= 7 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
                                            }`}
                                        >
                                            da {a.waitingDays} {a.waitingDays === 1 ? "giorno" : "giorni"}
                                        </span>
                                        <span className="text-slate-500">
                                            emessa il {(a.created_at || "").slice(0, 10).split("-").reverse().join("/")}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Cruscotto dichiarazioni TFR in attesa di firma */}
            {unsignedTfrDecls.length > 0 && (
                <div className="rounded-lg border border-sky-300 bg-sky-50/70 p-3" data-testid="tfr-decl-dashboard">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <FileSignature className="h-4 w-4 text-sky-600" />
                            <span className="font-semibold text-sky-900">
                                {unsignedTfrDecls.length === 1
                                    ? "1 dichiarazione T.F.R. in attesa di firma"
                                    : `${unsignedTfrDecls.length} dichiarazioni T.F.R. in attesa di firma`}
                            </span>
                            <span className="text-xs text-sky-700">
                                — la più vecchia da {unsignedTfrDecls[0].waitingDays}{" "}
                                {unsignedTfrDecls[0].waitingDays === 1 ? "giorno" : "giorni"}
                            </span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 border-sky-300 bg-white px-2.5 text-xs text-sky-800 hover:bg-sky-100"
                            onClick={() => setTfrPanelOpen(!tfrPanelOpen)}
                            data-testid="tfr-decl-dashboard-toggle"
                        >
                            {tfrPanelOpen ? "Nascondi elenco" : "Mostra elenco"}
                        </Button>
                    </div>
                    {tfrPanelOpen && (
                        <ul className="mt-2 divide-y divide-sky-100 rounded-md bg-white shadow-sm ring-1 ring-sky-100">
                            {unsignedTfrDecls.map((a) => (
                                <li
                                    key={a.id}
                                    className="flex cursor-pointer flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-sky-50"
                                    onClick={() => navigate(`/acconti/${a.id}`)}
                                    data-testid={`tfr-decl-pending-row-${a.id}`}
                                >
                                    <div className="min-w-0">
                                        <b>#{a.receipt_label}</b>
                                        <span className="ml-2">{a.employee_label}</span>
                                        <span className="ml-2 font-semibold text-slate-700">{fmtEur(a.amount)}</span>
                                        {a.signed_at ? (
                                            <span className="ml-2 text-xs text-emerald-600">ricevuta firmata</span>
                                        ) : (
                                            <span className="ml-2 text-xs text-amber-600">anche ricevuta da firmare</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <span
                                            className={`rounded-full px-2 py-0.5 font-semibold ${
                                                a.waitingDays >= 7 ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-800"
                                            }`}
                                        >
                                            da {a.waitingDays} {a.waitingDays === 1 ? "giorno" : "giorni"}
                                        </span>
                                        <span className="text-slate-500">
                                            emessa il {(a.created_at || "").slice(0, 10).split("-").reverse().join("/")}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {/* Filters */}
            <Card>
                <CardContent className="py-3">
                    <div className="grid gap-2 md:grid-cols-6">
                        <div>
                            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Anno</Label>
                            <Select value={year} onValueChange={setYear}>
                                <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="filter-year"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti gli anni</SelectItem>
                                    {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Tipo</Label>
                            <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
                                <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="filter-type"><SelectValue placeholder="Tutti" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i tipi</SelectItem>
                                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Stato</Label>
                            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
                                <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="filter-status"><SelectValue placeholder="Tutti" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti gli stati</SelectItem>
                                    {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Dipendente</Label>
                            <Select value={employeeId || "all"} onValueChange={(v) => setEmployeeId(v === "all" ? "" : v)}>
                                <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="filter-employee"><SelectValue placeholder="Tutti" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti</SelectItem>
                                    {employees.map((e) => (
                                        <SelectItem key={e.id} value={e.id}>
                                            {e.cognome} {e.nome}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Ricerca</Label>
                            <div className="relative mt-0.5">
                                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Numero, dipendente..."
                                    className="h-8 pl-8 text-xs"
                                    data-testid="filter-search"
                                />
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2 pt-3">
                    <CardTitle className="text-xs font-semibold uppercase tracking-wide text-slate-600">Elenco ricevute</CardTitle>
                    <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading} className="h-7 gap-1 text-[11px]" data-testid="refresh-btn">
                        <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
                        Aggiorna
                    </Button>
                </CardHeader>
                <CardContent className="pt-0">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-12 text-center text-sm text-slate-500">
                            Nessun acconto trovato per i filtri selezionati.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow className="text-xs">
                                    <SortableHead sortKey="receipt_label" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8">
                                        Ric.
                                    </SortableHead>
                                    <SortableHead sortKey="date" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8">
                                        Data
                                    </SortableHead>
                                    <SortableHead sortKey="dipendente" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8">
                                        Dipendente
                                    </SortableHead>
                                    <SortableHead sortKey="tipo" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8">
                                        Tipo
                                    </SortableHead>
                                    <SortableHead sortKey="amount" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8 text-right">
                                        Importo
                                    </SortableHead>
                                    <SortableHead sortKey="recupero" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8">
                                        Recupero
                                    </SortableHead>
                                    <SortableHead sortKey="progress_pct" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8">
                                        Progresso
                                    </SortableHead>
                                    <SortableHead sortKey="firma" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="h-8">
                                        Firma
                                    </SortableHead>
                                    <TableHead className="h-8"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {itemsSorted.map((a) => (
                                    <TableRow
                                        key={a.id}
                                        className="cursor-pointer text-xs hover:bg-slate-50"
                                        onClick={() => navigate(`/acconti/${a.id}`)}
                                        data-testid={`advance-row-${a.id}`}
                                    >
                                        <TableCell className="py-1.5 font-mono text-xs font-semibold">
                                            {a.receipt_label}
                                            {a.is_carryover_from_previous_year && (
                                                <Badge className="ml-1 border-amber-300 bg-amber-50 px-1 text-[9px] font-bold text-amber-700" variant="outline">
                                                    riporto
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="py-1.5">{fmtDate(a.date)}</TableCell>
                                        <TableCell className="py-1.5">
                                            <div className="font-medium text-slate-800">{a.employee_label}</div>
                                            <div className="text-[10px] text-slate-500">mat. {a.matricola || "—"}</div>
                                        </TableCell>
                                        <TableCell className="py-1.5">
                                            <span className="text-xs">{a.type_label}</span>
                                        </TableCell>
                                        <TableCell className="py-1.5 text-right font-mono font-semibold text-[#15498F]">
                                            {fmtEur(a.amount)}
                                            {a.installments_total > 1 && (
                                                <div className="text-[10px] font-normal text-slate-500">
                                                    {a.installment_plan ? planLabel(a.installment_plan) : `${a.installments_total} × ${fmtEur(a.installments_amount)}`}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="py-1.5 text-[11px] text-slate-600">
                                            {a.bp_init_label || "—"}
                                            {a.bp_final_label && a.bp_final_label !== a.bp_init_label && (
                                                <div className="text-[10px] text-slate-400">fino {a.bp_final_label}</div>
                                            )}
                                        </TableCell>
                                        <TableCell className="py-1.5">
                                            <RecoveryProgress advance={a} />
                                        </TableCell>
                                        <TableCell className="py-1.5">
                                            <SignatureBadge advance={a} />
                                        </TableCell>
                                        <TableCell className="py-1.5">
                                            <div className="flex items-center gap-0.5">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={(e) => { e.stopPropagation(); setPreviewAdvance(a); }}
                                                    title="Anteprima PDF"
                                                    data-testid={`row-pdf-${a.id}`}
                                                >
                                                    <FileSearch className="h-3.5 w-3.5 text-[#15498F]" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={(e) => { e.stopPropagation(); navigate(`/acconti/${a.id}`); }}
                                                    title="Apri dettaglio"
                                                >
                                                    <Eye className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <CreateAdvanceDialog
                open={createOpen || !!approveRequest}
                onClose={() => { setCreateOpen(false); setApproveRequest(null); }}
                employees={employees}
                fromRequest={approveRequest}
                onCreated={(adv) => {
                    setCreateOpen(false);
                    setApproveRequest(null);
                    loadRequests();
                    toast.success(`Ricevuta #${adv.receipt_label} creata`);
                    navigate(`/acconti/${adv.id}`);
                }}
            />
            <Dialog open={!!rejectRequest} onOpenChange={(o) => { if (!o) setRejectRequest(null); }}>
                <DialogContent className="max-w-md" data-testid="advance-reject-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">Rifiuta richiesta di acconto</DialogTitle>
                        <DialogDescription>
                            {rejectRequest && (
                                <>Richiesta di <b>{rejectRequest.employee_label}</b> per{" "}
                                <b>€ {Number(rejectRequest.amount).toFixed(2)}</b>. Il dipendente vedrà l'esito
                                (e l'eventuale motivazione) nella sua sezione Acconti.</>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="reject-note">Motivazione (facoltativa)</Label>
                        <Input
                            id="reject-note"
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            placeholder="es. Importo non erogabile questo mese"
                            className="mt-1"
                            data-testid="advance-reject-note-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectRequest(null)}>Annulla</Button>
                        <Button
                            onClick={handleReject}
                            disabled={rejectBusy}
                            className="gap-1.5 bg-red-600 text-white hover:bg-red-700"
                            data-testid="advance-reject-confirm"
                        >
                            {rejectBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                            Rifiuta richiesta
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ExportReportDialog
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                defaultYear={parseInt(year, 10)}
                employees={employees}
            />
            <AdvancePdfPreviewDialog
                advance={previewAdvance}
                open={!!previewAdvance}
                onClose={() => setPreviewAdvance(null)}
            />
        </div>
    );
}


function KpiCard({ icon: Icon, label, value, color, testId, onClick, active }) {
    const colorMap = {
        blue: "from-[#376DB3] to-[#15498F]",
        emerald: "from-emerald-500 to-emerald-700",
        amber: "from-amber-500 to-amber-700",
        slate: "from-slate-500 to-slate-700",
    };
    const interactive = typeof onClick === "function";
    return (
        <button
            type="button"
            onClick={interactive ? onClick : undefined}
            disabled={!interactive}
            className={`relative overflow-hidden rounded-lg bg-gradient-to-br ${colorMap[color]} px-3 py-2.5 text-left text-white shadow-sm transition-all ${
                interactive ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70" : "cursor-default"
            } ${active ? "ring-2 ring-white/90 shadow-lg" : ""}`}
            data-testid={testId}
            aria-pressed={interactive ? !!active : undefined}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="text-[9px] font-semibold uppercase tracking-wider text-white/80">{label}</div>
                    <div className="mt-0.5 font-display text-lg font-bold leading-tight">{value}</div>
                </div>
                <Icon className="h-5 w-5 flex-shrink-0 text-white/40" />
            </div>
        </button>
    );
}


function CreateAdvanceDialog({ open, onClose, employees, onCreated, fromRequest }) {
    const activeEmployees = useMemo(
        () => employees.filter((e) => !e.data_cessazione),
        [employees]
    );
    const today = new Date().toISOString().slice(0, 10);
    // Default recovery payslip = current month + year (es. "B.P. 02/2026")
    const todayDate = new Date();
    const defaultPayslip = `B.P. ${String(todayDate.getMonth() + 1).padStart(2, "0")}/${todayDate.getFullYear()}`;
    const [form, setForm] = useState({
        employee_id: "",
        amount: "",
        date: today,
        type: "ACCONTO",
        payment_method: "BON_ISTAN",
        payment_account: "SU C/C PERSONALE",
        bank_name: "",
        recovery_payslip: defaultPayslip,
        recovery_rates: 1,
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [nextNumber, setNextNumber] = useState(null);
    const [planChoice, setPlanChoice] = useState("exact");
    const [customSchedule, setCustomSchedule] = useState(false);
    const [schedRows, setSchedRows] = useState([]);
    const nRates = parseInt(form.recovery_rates, 10) || 1;
    const amountNum = parseFloat(form.amount) || 0;
    const planProposals = useMemo(() => buildPlanProposals(amountNum, nRates), [amountNum, nRates]);

    useEffect(() => {
        setPlanChoice("exact");
        if (nRates <= 1) setCustomSchedule(false);
    }, [amountNum, nRates]);

    // Prefill the per-rata rows when custom months get enabled / base data changes
    useEffect(() => {
        if (!customSchedule || nRates <= 1) return;
        const labels = buildConsecutiveLabels(form.recovery_payslip, nRates);
        const chosen = planChoice !== "exact" && planProposals[parseInt(planChoice, 10)]
            ? planProposals[parseInt(planChoice, 10)]
            : evenSplit(amountNum, nRates);
        setSchedRows(labels.map((bp, i) => ({ bp, amount: String(chosen[i] ?? "") })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customSchedule, nRates, form.recovery_payslip]);

    useEffect(() => {
        if (open) {
            const reqMonths = fromRequest?.recovery_target === "BUSTA_PAGA" ? (fromRequest.recovery_months_count || 1) : 1;
            const reqType = !fromRequest ? "ACCONTO"
                : fromRequest.recovery_target === "FNG" ? "FNG"
                : fromRequest.recovery_target === "TFR" ? "TFR"
                : reqMonths > 1 ? "PRESTITO" : "ACCONTO";
            setForm({
                employee_id: fromRequest?.employee_id || "",
                amount: fromRequest ? String(fromRequest.amount) : "",
                date: today,
                type: reqType,
                payment_method: fromRequest?.preferred_payment_method || "BON_ISTAN",
                payment_account: "SU C/C PERSONALE",
                bank_name: "",
                recovery_payslip: fromRequest?.recovery_month ? `B.P. ${fromRequest.recovery_month}` : defaultPayslip,
                recovery_rates: reqMonths,
                notes: fromRequest ? `Da richiesta dipendente: ${fromRequest.reason}` : "",
            });
            setNextNumber(null);
            setPlanChoice("exact");
            setCustomSchedule(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Preview the next receipt number for the year of the selected date
    useEffect(() => {
        if (!open || !form.date) return;
        const year = new Date(form.date).getFullYear();
        if (Number.isNaN(year)) return;
        let cancelled = false;
        api.get("/advances/next-number", { params: { year } })
            .then(({ data }) => {
                if (!cancelled) setNextNumber({ year: data.year, n: data.next_number });
            })
            .catch(() => { if (!cancelled) setNextNumber(null); });
        return () => { cancelled = true; };
    }, [open, form.date]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.employee_id) {
            toast.error("Seleziona un dipendente");
            return;
        }
        if (!form.amount || parseFloat(form.amount) <= 0) {
            toast.error("Importo non valido");
            return;
        }
        setSubmitting(true);
        try {
            const body = {
                ...form,
                amount: parseFloat(form.amount),
                recovery_rates: parseInt(form.recovery_rates, 10) || 1,
                installment_plan: planChoice === "exact" ? undefined : planProposals[parseInt(planChoice, 10)],
            };
            if (nRates > 1 && customSchedule) {
                const err = validateScheduleRows(schedRows, amountNum);
                if (err) {
                    toast.error(err);
                    setSubmitting(false);
                    return;
                }
                body.installment_plan = schedRows.map((r) => round2(parseFloat(r.amount)));
                body.installment_schedule = schedRows.map((r) => r.bp);
            }
            let data;
            if (fromRequest) {
                const { data: reviewed } = await api.post(
                    `/advance-requests/${fromRequest.id}/review`,
                    { action: "approve", advance_data: body }
                );
                const { data: adv } = await api.get(`/advances/${reviewed.approved_advance_id}`);
                data = adv;
            } else {
                const res = await api.post("/advances", body);
                data = res.data;
            }
            onCreated(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore creazione acconto");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="create-advance-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between gap-3 font-display text-[#15498F]">
                        <span className="flex items-center gap-2">
                            <Banknote className="h-5 w-5" />
                            Nuovo acconto
                        </span>
                        {nextNumber && (
                            <span
                                className="inline-flex items-center gap-1 rounded-full bg-[#5BAEDB]/15 px-3 py-1 text-xs font-semibold tracking-wide text-[#15498F] ring-1 ring-[#5BAEDB]/30"
                                data-testid="next-receipt-number"
                            >
                                Sarà la ricevuta n. {nextNumber.n}/{nextNumber.year}
                            </span>
                        )}
                    </DialogTitle>
                    <DialogDescription>
                        La ricevuta viene numerata automaticamente in modo progressivo per l'anno corrente.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <Label className="text-xs">Dipendente *</Label>
                            <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                                <SelectTrigger className="mt-1" data-testid="create-emp"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                                <SelectContent>
                                    {activeEmployees.map((e) => (
                                        <SelectItem key={e.id} value={e.id}>
                                            {e.cognome} {e.nome} (mat. {e.matricola || "—"})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Data ricevuta *</Label>
                            <Input
                                type="date"
                                value={form.date}
                                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                                className="mt-1"
                                required
                                data-testid="create-date"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Importo (€) *</Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={form.amount}
                                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                                placeholder="300.00"
                                className="mt-1"
                                required
                                data-testid="create-amount"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Tipo *</Label>
                            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                                <SelectTrigger className="mt-1" data-testid="create-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Modalità pagamento *</Label>
                            <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
                                <SelectTrigger className="mt-1" data-testid="create-method"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Dettaglio pagamento</Label>
                            <Input
                                value={form.payment_account}
                                onChange={(e) => setForm((f) => ({ ...f, payment_account: e.target.value }))}
                                placeholder="C/C personale, IBAN, n° assegno..."
                                className="mt-1"
                                data-testid="create-account"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Busta paga di recupero</Label>
                            <div className="mt-1">
                                <BustaPagaSelector
                                    value={form.recovery_payslip}
                                    onChange={(v) => setForm((f) => ({ ...f, recovery_payslip: v }))}
                                    testidPrefix="create"
                                />
                            </div>
                            <p className="mt-1 text-[10px] text-slate-500">
                                14 buste paga: 12 mensili + <b>Tredicesima (XIII)</b> + <b>Quattordicesima (XIV)</b>
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs">Numero rate</Label>
                            <Input
                                type="number"
                                min="1"
                                max="24"
                                value={form.recovery_rates}
                                onChange={(e) => setForm((f) => ({ ...f, recovery_rates: e.target.value }))}
                                className="mt-1"
                                data-testid="create-rates"
                            />
                        </div>
                    </div>
                    {["TFR", "ACCONTO_TFR"].includes(form.type) && (
                        <TfrBalanceBox employeeId={form.employee_id} year={form.recovery_payslip?.match(/\/(\d{4})$/)?.[1]} amount={form.amount} />
                    )}
                    {nRates > 1 && amountNum > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="plan-proposals-box">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <Label className="text-xs font-semibold text-slate-700">Piano rate</Label>
                                <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-slate-700">
                                    <input
                                        type="checkbox"
                                        checked={customSchedule}
                                        onChange={(e) => setCustomSchedule(e.target.checked)}
                                        data-testid="create-custom-schedule-toggle"
                                    />
                                    Personalizza mesi e importi delle rate
                                </label>
                            </div>
                            {customSchedule ? (
                                <>
                                    <p className="mb-2 mt-0.5 text-[10px] text-slate-500">
                                        Scegli la busta paga di ogni rata (anche mesi non consecutivi, XIII/XIV incluse) e l'importo. Massimo una rata per busta paga.
                                    </p>
                                    <InstallmentScheduleEditor
                                        rows={schedRows}
                                        onChange={setSchedRows}
                                        totalAmount={amountNum}
                                        testidPrefix="create-sched"
                                    />
                                </>
                            ) : planProposals.length > 0 ? (
                                <>
                                    <p className="mb-2 mt-0.5 text-[10px] text-slate-500">
                                        La rata esatta non è «tonda»: puoi scegliere un piano con rate arrotondate (le rate più alte in coda).
                                    </p>
                                    <div className="space-y-1.5">
                                        <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 has-[:checked]:border-[#376DB3] has-[:checked]:bg-[#376DB3]/5">
                                            <input
                                                type="radio"
                                                name="plan-choice"
                                                checked={planChoice === "exact"}
                                                onChange={() => setPlanChoice("exact")}
                                                data-testid="plan-exact"
                                            />
                                            <span>Rata esatta: {nRates} × {fmtEur(amountNum / nRates)}</span>
                                        </label>
                                        {planProposals.map((p, i) => (
                                            <label key={planLabel(p)} className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 has-[:checked]:border-[#376DB3] has-[:checked]:bg-[#376DB3]/5">
                                                <input
                                                    type="radio"
                                                    name="plan-choice"
                                                    checked={planChoice === String(i)}
                                                    onChange={() => setPlanChoice(String(i))}
                                                    data-testid={`plan-option-${i}`}
                                                />
                                                <span className="font-semibold">{planLabel(p)}</span>
                                            </label>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p className="mt-1 text-[10px] text-slate-500">
                                    Rate mensili consecutive da {form.recovery_payslip || "—"}: {nRates} × {fmtEur(amountNum / nRates)}
                                </p>
                            )}
                        </div>
                    )}
                    <div>
                        <Label className="text-xs">Note (opzionale)</Label>
                        <Input
                            value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            placeholder="Eventuali annotazioni..."
                            className="mt-1"
                            data-testid="create-notes"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="gap-2 bg-[#15498F] text-white hover:bg-[#15498F]/90"
                            data-testid="create-submit-btn"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                            Crea ricevuta
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}



const ALL_COLUMNS = [
    { key: "receipt_label", label: "N. Ricevuta", default: true },
    { key: "date", label: "Data", default: true },
    { key: "matricola", label: "Matricola", default: true },
    { key: "employee_label", label: "Dipendente", default: true },
    { key: "type", label: "Tipo acconto", default: true },
    { key: "amount", label: "Importo €", default: true },
    { key: "amount_in_words", label: "Importo in lettere", default: false },
    { key: "payment_method", label: "Modalità pagamento", default: false },
    { key: "payment_account", label: "Dettaglio pagamento", default: false },
    { key: "recovery_payslip", label: "Recupero su B.P.", default: true },
    { key: "recovery_rates", label: "N. rate", default: false },
    { key: "status", label: "Stato", default: true },
    { key: "notes", label: "Note", default: false },
];

const MONTHS = [
    { v: 1, l: "Gennaio" }, { v: 2, l: "Febbraio" }, { v: 3, l: "Marzo" },
    { v: 4, l: "Aprile" }, { v: 5, l: "Maggio" }, { v: 6, l: "Giugno" },
    { v: 7, l: "Luglio" }, { v: 8, l: "Agosto" }, { v: 9, l: "Settembre" },
    { v: 10, l: "Ottobre" }, { v: 11, l: "Novembre" }, { v: 12, l: "Dicembre" },
];

// Months including special XIII (Tredicesima 20/12) and XIV (Quattordicesima 20/07).
// Used for the BP "Recupero per Busta Paga" report which includes these special slots.
const BP_MONTHS = [
    ...MONTHS,
    { v: 14, l: "XIV — Quattordicesima (20/07)", special: true },
    { v: 13, l: "XIII — Tredicesima (20/12)", special: true },
];


function ExportReportDialog({ open, onClose, defaultYear, employees }) {
    const [mode, setMode] = useState("payslip"); // "payslip" or "yearly"
    const [year, setYear] = useState(defaultYear || new Date().getFullYear());
    const [bpMonth, setBpMonth] = useState(new Date().getMonth() + 1);
    const [bpYear, setBpYear] = useState(new Date().getFullYear());
    const [months, setMonths] = useState([]);
    const [columns, setColumns] = useState(ALL_COLUMNS.filter((c) => c.default).map((c) => c.key));
    const [format, setFormat] = useState("pdf");
    const [submitting, setSubmitting] = useState(false);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (open) {
            setMode("payslip");
            setYear(defaultYear || new Date().getFullYear());
            const now = new Date();
            // Default: previous month BP (which is being processed this month)
            const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth();
            setBpMonth(prevMonth);
            setBpYear(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
            setMonths([]);
            setColumns(ALL_COLUMNS.filter((c) => c.default).map((c) => c.key));
            setFormat("pdf");
            setPreview(null);
        }
    }, [open, defaultYear]);

    // Load preview of withholdings when in "payslip" mode
    useEffect(() => {
        if (!open || mode !== "payslip") return;
        let cancelled = false;
        api.get("/advances/reports/payslip-recovery", {
            params: { bp_month: bpMonth, bp_year: bpYear },
        }).then(({ data }) => {
            if (!cancelled) setPreview(data);
        }).catch(() => { if (!cancelled) setPreview(null); });
        return () => { cancelled = true; };
    }, [open, mode, bpMonth, bpYear]);

    const toggleColumn = (key) => {
        setColumns((c) => c.includes(key) ? c.filter((x) => x !== key) : [...c, key]);
    };
    const toggleMonth = (m) => {
        setMonths((curr) => curr.includes(m) ? curr.filter((x) => x !== m) : [...curr, m]);
    };

    const handleYearlyExport = async () => {
        if (columns.length === 0) {
            toast.error("Seleziona almeno una colonna");
            return;
        }
        setSubmitting(true);
        try {
            const r = await api.post(
                "/advances/reports/export",
                { year, months: months.length ? months : null, include_columns: columns, format },
                { responseType: "blob" },
            );
            const mimeType = format === "csv" ? "text/csv;charset=utf-8" : "application/pdf";
            const url = window.URL.createObjectURL(new Blob([r.data], { type: mimeType }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Riepilogo_acconti_${year}.${format === "csv" ? "csv" : "pdf"}`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Report generato");
            onClose();
        } catch (e) {
            toast.error("Errore export");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePivotExport = async () => {
        setSubmitting(true);
        try {
            const r = await api.get(
                "/advances/reports/annual-pivot",
                { params: { year: bpYear, fmt: format }, responseType: "blob" },
            );
            const mimeType = format === "csv" ? "text/csv;charset=utf-8" : "application/pdf";
            const url = window.URL.createObjectURL(new Blob([r.data], { type: mimeType }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Riepilogo_annuale_${bpYear}.${format === "csv" ? "csv" : "pdf"}`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Report pivot generato");
            onClose();
        } catch (e) {
            toast.error("Errore pivot");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePayslipExport = async () => {
        // Use the yearly export endpoint with month filter set to bpMonth — this gives all
        // receipts whose date is in that month. But the user wants the WITHHOLDING list
        // (which can include receipts issued months ago for ongoing loans).
        // For now we generate the same view as the preview, as PDF.
        if (!preview) {
            toast.error("Nessun dato da esportare");
            return;
        }
        setSubmitting(true);
        try {
            // Reuse the export endpoint by passing employee_ids with selected ones?
            // Actually simpler: dedicated endpoint expecting bp_month + bp_year
            const r = await api.post(
                "/advances/reports/export-payslip",
                { bp_month: bpMonth, bp_year: bpYear, format, include_columns: columns },
                { responseType: "blob" },
            );
            const mimeType = format === "csv" ? "text/csv;charset=utf-8" : "application/pdf";
            const url = window.URL.createObjectURL(new Blob([r.data], { type: mimeType }));
            const a = document.createElement("a");
            a.href = url;
            const monthLabel = bpMonth === 14 ? "XIV" : bpMonth === 13 ? "XIII" : String(bpMonth).padStart(2, "0");
            a.download = `Recupero_BP_${monthLabel}_${bpYear}.${format === "csv" ? "csv" : "pdf"}`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Report generato");
            onClose();
        } catch (e) {
            toast.error("Errore export");
        } finally {
            setSubmitting(false);
        }
    };

    const years = useMemo(() => {
        const cur = new Date().getFullYear();
        const list = [];
        for (let y = cur + 1; y >= cur - 4; y--) list.push(String(y));
        return list;
    }, []);

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="export-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display text-base text-[#15498F]">
                        <FileSpreadsheet className="h-4 w-4" />
                        Riepilogo per consulente del lavoro
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Scegli il tipo di report. Il riepilogo per busta paga elenca <strong>quanto trattenere a ciascun dipendente</strong> per il cedolino del mese.
                    </DialogDescription>
                </DialogHeader>

                {/* Mode tabs */}
                <div className="flex gap-1 rounded-md bg-slate-100 p-1">
                    <button
                        type="button"
                        onClick={() => setMode("payslip")}
                        className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                            mode === "payslip" ? "bg-white text-[#15498F] shadow-sm" : "text-slate-500"
                        }`}
                        data-testid="mode-payslip"
                    >
                        Per busta paga
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("pivot")}
                        className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                            mode === "pivot" ? "bg-white text-[#15498F] shadow-sm" : "text-slate-500"
                        }`}
                        data-testid="mode-pivot"
                    >
                        Pivot annuale (14 colonne)
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("yearly")}
                        className={`flex-1 rounded px-3 py-1.5 text-xs font-medium transition-colors ${
                            mode === "yearly" ? "bg-white text-[#15498F] shadow-sm" : "text-slate-500"
                        }`}
                        data-testid="mode-yearly"
                    >
                        Dettagliato
                    </button>
                </div>

                {mode === "payslip" ? (
                    <div className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-3">
                            <div>
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Mese BP</Label>
                                <Select value={String(bpMonth)} onValueChange={(v) => setBpMonth(parseInt(v, 10))}>
                                    <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="bp-month"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {BP_MONTHS.map((m) => (
                                            <SelectItem key={m.v} value={String(m.v)}>
                                                {m.special ? (
                                                    <span className="flex items-center gap-1.5">
                                                        <span className="rounded bg-amber-100 px-1 py-0.5 text-[8px] font-bold uppercase text-amber-700">Speciale</span>
                                                        {m.l}
                                                    </span>
                                                ) : m.l}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Anno BP</Label>
                                <Select value={String(bpYear)} onValueChange={(v) => setBpYear(parseInt(v, 10))}>
                                    <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="bp-year"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Formato</Label>
                                <div className="mt-0.5 flex gap-1">
                                    <Button type="button" variant={format === "pdf" ? "default" : "outline"} onClick={() => setFormat("pdf")} className={`h-8 text-xs ${format === "pdf" ? "bg-[#15498F]" : ""}`} size="sm">PDF</Button>
                                    <Button type="button" variant={format === "csv" ? "default" : "outline"} onClick={() => setFormat("csv")} className={`h-8 text-xs ${format === "csv" ? "bg-[#15498F]" : ""}`} size="sm">CSV</Button>
                                </div>
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="payslip-preview">
                            {preview ? (
                                <>
                                    <div className="flex items-center justify-between text-xs">
                                        <div>
                                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Anteprima</div>
                                            <div className="font-display text-lg font-bold text-[#15498F]">
                                                {preview.bp_label}
                                            </div>
                                            <div className="text-[10px] text-slate-500">
                                                {bpMonth === 14 ? `Quattordicesima — erogata il 20/07/${bpYear}` :
                                                 bpMonth === 13 ? `Tredicesima — erogata il 20/12/${bpYear}` :
                                                 `Cedolino emesso il 15/${String((bpMonth + 1) > 12 ? 1 : (bpMonth + 1)).padStart(2, "0")}/${(bpMonth + 1) > 12 ? bpYear + 1 : bpYear}`}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Totale da trattenere</div>
                                            <div className="font-display text-xl font-bold text-[#15498F]">{fmtEur(preview.grand_total)}</div>
                                            {preview.transfer_costs_total > 0 && (
                                                <div className="text-[10px] font-semibold text-amber-700" data-testid="preview-transfer-costs">
                                                    + spese bonifico {fmtEur(preview.transfer_costs_total)}
                                                </div>
                                            )}
                                            <div className="text-[10px] text-slate-500">{preview.employees_count} dipendenti</div>
                                        </div>
                                    </div>
                                    {preview.unplaced?.length > 0 && (
                                        <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-[11px] text-red-700" data-testid="preview-unplaced">
                                            <div className="font-semibold">
                                                Attenzione: {preview.unplaced.length} ricevute NON incluse (busta paga di recupero mancante o non riconoscibile)
                                            </div>
                                            <ul className="mt-1 list-disc pl-4">
                                                {preview.unplaced.map((u) => (
                                                    <li key={u.advance_id}>
                                                        Ric. {u.receipt_label} — {u.employee_label} — {fmtEur(u.amount)} — B.P. indicata: {u.recovery_payslip}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {preview.by_employee.length === 0 ? (
                                        <p className="mt-3 text-center text-xs text-slate-500">Nessuna trattenuta prevista per questo cedolino.</p>
                                    ) : (
                                        <div className="mt-3 max-h-48 overflow-y-auto rounded border border-slate-200 bg-white">
                                            <table className="w-full text-[11px]">
                                                <thead className="sticky top-0 bg-slate-100">
                                                    <tr>
                                                        <th className="px-2 py-1 text-left font-semibold">Dipendente</th>
                                                        <th className="px-2 py-1 text-center font-semibold">Rate</th>
                                                        <th className="px-2 py-1 text-right font-semibold">Trattenuta</th>
                                                        <th className="px-2 py-1 text-right font-semibold">Spese</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {preview.by_employee.map((e) => (
                                                        <tr key={e.employee_id} className={`border-t border-slate-100 ${e.is_terminated ? "bg-rose-50" : ""}`}>
                                                            <td className="px-2 py-1">
                                                                <span className="font-medium">{e.employee_label}</span>
                                                                {e.matricola && <span className="ml-1 text-[10px] text-slate-500">m.{e.matricola}</span>}
                                                                {e.is_terminated && (
                                                                    <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
                                                                        Cessato · ult. {e.termination_last_bp?.replace("B.P. ", "")}
                                                                    </span>
                                                                )}
                                                                {e.items?.some((i) => i.is_anticipated_termination) && (
                                                                    <div className="mt-0.5 text-[9px] text-rose-700">
                                                                        ⚠ Recupero integrale anticipato in questa B.P.
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-1 text-center text-slate-600">{e.receipts_count}</td>
                                                            <td className="px-2 py-1 text-right font-mono font-semibold">{fmtEur(e.amount_total)}</td>
                                                            <td className="px-2 py-1 text-right font-mono text-slate-500">{e.transfer_cost_total > 0 ? fmtEur(e.transfer_cost_total) : "—"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                    {/* Subtotals by causale */}
                                    {preview.subtotals_by_causale?.length > 0 && (
                                        <div className="mt-3 rounded border border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50/40 p-2">
                                            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#15498F]">
                                                Subtotali per causale
                                            </div>
                                            <div className="space-y-0.5">
                                                {preview.subtotals_by_causale.map((s) => (
                                                    <div key={s.causale} className="flex items-center justify-between text-[11px]">
                                                        <span className="font-medium text-slate-700">
                                                            {s.causale}
                                                            <span className="ml-1 text-[10px] text-slate-400">({s.count})</span>
                                                        </span>
                                                        <span className="font-mono font-semibold text-[#15498F]">{fmtEur(s.amount)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
                            )}
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose} size="sm">Annulla</Button>
                            <Button
                                onClick={handlePayslipExport}
                                disabled={submitting || !preview || preview.employees_count === 0}
                                className="gap-2 bg-[#15498F] text-white hover:bg-[#15498F]/90"
                                size="sm"
                                data-testid="export-payslip-btn"
                            >
                                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                                Genera {format.toUpperCase()}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : mode === "pivot" ? (
                    <div className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-2">
                            <div>
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Anno</Label>
                                <Select value={String(bpYear)} onValueChange={(v) => setBpYear(parseInt(v, 10))}>
                                    <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="pivot-year"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Formato</Label>
                                <div className="mt-0.5 flex gap-1">
                                    <Button type="button" variant={format === "pdf" ? "default" : "outline"} onClick={() => setFormat("pdf")} className={`h-8 text-xs ${format === "pdf" ? "bg-[#15498F]" : ""}`} size="sm">PDF</Button>
                                    <Button type="button" variant={format === "csv" ? "default" : "outline"} onClick={() => setFormat("csv")} className={`h-8 text-xs ${format === "csv" ? "bg-[#15498F]" : ""}`} size="sm">CSV</Button>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-md border border-slate-200 bg-gradient-to-br from-slate-50 to-blue-50 p-4">
                            <div className="flex items-start gap-3">
                                <div className="rounded-full bg-[#15498F] p-2 text-white">
                                    <FileSpreadsheet className="h-4 w-4" />
                                </div>
                                <div className="flex-1 text-xs leading-relaxed text-slate-700">
                                    <p className="mb-1 font-semibold text-[#15498F]">Riepilogo annuale acconti — tabella pivot</p>
                                    <p>Una riga per dipendente con <b>14 colonne mensili</b> in ordine cronologico:</p>
                                    <p className="mt-1 font-mono text-[10px] text-slate-600">
                                        Gen · Feb · Mar · Apr · Mag · Giu · <span className="rounded bg-amber-100 px-1 text-amber-700">XIV</span> · Lug · Ago · Set · Ott · Nov · <span className="rounded bg-amber-100 px-1 text-amber-700">XIII</span> · Dic
                                    </p>
                                    <p className="mt-2 text-[10px] text-slate-600">
                                        Ogni cella mostra l'importo trattenuto al dipendente in quella busta paga.
                                        I dipendenti cessati appaiono evidenziati e contribuiscono solo fino alla loro ultima B.P. utile.
                                        Le colonne XIII (Tredicesima, 20/12) e XIV (Quattordicesima, 20/07) sono evidenziate in arancione.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose} size="sm">Annulla</Button>
                            <Button
                                onClick={handlePivotExport}
                                disabled={submitting}
                                className="gap-2 bg-[#15498F] text-white hover:bg-[#15498F]/90"
                                size="sm"
                                data-testid="export-pivot-btn"
                            >
                                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                                Genera {format.toUpperCase()}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid gap-3 md:grid-cols-2">
                            <div>
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Anno</Label>
                                <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                                    <SelectTrigger className="mt-0.5 h-8 text-xs" data-testid="export-year"><SelectValue /></SelectTrigger>
                                    <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Formato</Label>
                                <div className="mt-0.5 flex gap-1">
                                    <Button type="button" variant={format === "pdf" ? "default" : "outline"} onClick={() => setFormat("pdf")} className={`h-8 text-xs ${format === "pdf" ? "bg-[#15498F]" : ""}`} size="sm" data-testid="export-format-pdf">PDF</Button>
                                    <Button type="button" variant={format === "csv" ? "default" : "outline"} onClick={() => setFormat("csv")} className={`h-8 text-xs ${format === "csv" ? "bg-[#15498F]" : ""}`} size="sm" data-testid="export-format-csv">CSV / Excel</Button>
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                                Mesi <span className="ml-1 font-normal text-slate-400 normal-case">(vuoto = tutto l'anno)</span>
                            </Label>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {MONTHS.map((m) => {
                                    const active = months.includes(m.v);
                                    return (
                                        <button key={m.v} type="button" onClick={() => toggleMonth(m.v)}
                                            className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${active ? "border-[#15498F] bg-[#15498F] text-white" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}
                                            data-testid={`month-${m.v}`}>
                                            {m.l.slice(0, 3)}
                                        </button>
                                    );
                                })}
                                {/* Special months XIV (Quattordicesima) and XIII (Tredicesima) */}
                                {[
                                    { v: 14, l: "XIV", title: "Quattordicesima (20/07)" },
                                    { v: 13, l: "XIII", title: "Tredicesima (20/12)" },
                                ].map((m) => {
                                    const active = months.includes(m.v);
                                    return (
                                        <button
                                            key={m.v}
                                            type="button"
                                            onClick={() => toggleMonth(m.v)}
                                            title={m.title}
                                            className={`rounded-md border px-2 py-1 text-[11px] font-bold transition-colors ${active ? "border-amber-600 bg-amber-500 text-white" : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
                                            data-testid={`month-${m.v}`}
                                        >
                                            {m.l}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <Label className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Colonne da includere</Label>
                            <div className="mt-1 grid gap-1.5 md:grid-cols-2">
                                {ALL_COLUMNS.map((c) => {
                                    const checked = columns.includes(c.key);
                                    return (
                                        <label key={c.key}
                                            className={`flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1.5 text-xs ${checked ? "border-[#5BAEDB] bg-[#5BAEDB]/10 text-[#15498F]" : "border-slate-200 bg-white text-slate-700"}`}
                                            data-testid={`col-${c.key}`}>
                                            <input type="checkbox" checked={checked} onChange={() => toggleColumn(c.key)} className="h-3.5 w-3.5 rounded border-slate-300 text-[#15498F] focus:ring-[#15498F]" />
                                            <span>{c.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={onClose} size="sm">Annulla</Button>
                            <Button onClick={handleYearlyExport} disabled={submitting || columns.length === 0}
                                className="gap-2 bg-[#15498F] text-white hover:bg-[#15498F]/90" size="sm" data-testid="export-submit-btn">
                                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />}
                                Genera {format.toUpperCase()}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
